import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { VisibilityPredicateType } from "../../../registry/visibility";
import { IntegrationKey } from "@erp/shared-types";

/**
 * Ingest AR Data from Famous Step
 *
 * Runs the full AR ingestion pipeline from Famous Oracle into Postgres:
 *   0. ensureArDependencies  — Companies, Customers, SalesOrders
 *   1. ingestArInvoices      — Invoice headers
 *   2. ingestArSalesOrderLineItems — SO line items
 *   3. ingestArInvoiceLineItems    — Invoice line items (product, charge, credit memo)
 *   4. ingestArPayments            — Payments from BANK_RECEIPT_TRX
 *   5. ingestArPaymentApplications — Payment-to-invoice links
 *
 * Self-contained: does not require a prior full Famous sync.
 *
 * Note: This is a backend-only step that requires Famous database access.
 */
export const ingestArDataStep = defineStepConfig({
  type: "ingest_ar_data",
  name: "Ingest AR Data from Famous",
  description:
    "Run the full AR ingestion pipeline — invoices, line items, payments, and applications",
  category: "Integration",
  libraryModule: "Famous",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({}),

  outputSchema: z.object({
    totalSuccess: z
      .number()
      .describe("Total records successfully ingested across all entity types"),
    totalFailed: z
      .number()
      .describe("Total records that failed to ingest across all entity types"),
    durationMs: z
      .number()
      .describe("Total wall-clock duration of the ingestion in milliseconds"),
  }),

  configSchema: z.object({
    syncScope: z
      .enum(["open", "all"])
      .default("open")
      .describe(
        "'open' syncs active invoices only; 'all' includes paid/closed (full backfill)",
      ),
    invoiceDateAfter: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
      .nullable()
      .default(null)
      .describe(
        "When set (YYYY-MM-DD), only ingest invoices on or after this date. Implies syncScope='all'.",
      ),
    invoiceDateBefore: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format")
      .nullable()
      .default(null)
      .describe(
        "When set (YYYY-MM-DD), only ingest invoices on or before this date. Implies syncScope='all'. " +
          "Combines with invoiceDateAfter to define an explicit range.",
      ),
    sinceDays: z
      .number()
      .int()
      .positive()
      .nullable()
      .default(null)
      .describe(
        "Window length in days. Implies syncScope='all'. " +
          "Alone: ingest the last N days from today (sets the lower bound). " +
          "With invoiceDateAfter: ingest a window of N days starting at the after date (sets the upper bound). " +
          "Ignored when invoiceDateBefore is set explicitly.",
      ),
    windowMode: z
      .enum(["invoiceDate", "updateTime"])
      .default("invoiceDate")
      .describe(
        "Controls what the days window filters on. " +
          "'invoiceDate' filters by invoice date (default). " +
          "'updateTime' filters by last-modified timestamp — catches status changes and payment updates on older invoices.",
      ),
  }),

  execution: {
    startToCloseTimeout: "120 minutes",
  },

  emits: [] as const,
});
